import { useEffect, useState } from "react";

import { ecoFuturesBankContractAddress, getReadOnlyContract, getReadOnlyProvider } from "../services/contract.service";
import { hasOpenChallenge } from "../services/challenge.service";

// What the verifier's re-verification page must know before offering Verify or Block on a minted
// covenant (EcoChallenge guide §3b, §3d, §4 — V10.1.2). All chain reads, all views, no wallet.
//
//   bankHoldsEft    ownerOf(tokenId) is EcoBank: the sale is settling (endAuction → finalise), and
//                   blockVerifiedEasement reverts EasementIsChallenged until it finalises. Block
//                   hides itself.
//   openChallenge   EcoChallenge.hasOpenChallenge(tokenId). Until the next contract release a
//                   re-verification opens a fresh window and wipes the open challenge's record
//                   from the current one (Ravi's review, finding 4), so Verify waits it out.
//   nextDueAt       after the sale, verifyEasement goes through EcoBank.ClaimFundNow, which
//                   reverts CannotClaimYet unless an instalment is due: easementVestingStartTime +
//                   (releasedCount + 1) × releaseFundInterval (every 12 h on testnet). Null when
//                   there is no such gate — not sold yet (vesting start 0), or the drip is frozen
//                   (verifyEasement then skips ClaimFundNow entirely, which is the cure path).
//
// A failed read leaves the gate open (null state) rather than blocking a verifier on an RPC
// hiccup: the contract still refuses, and the relay error names the revert.

export interface ReVerificationGate {
  bankHoldsEft: boolean;
  openChallenge: boolean;
  nextDueAt: Date | null;
  // Chain time minus wall-clock time at the read, in ms — compare nextDueAt against
  // Date.now() + this, not the viewer's clock.
  chainClockOffsetMs: number;
}

export const fetchReVerificationGate = async (tokenId: number | string): Promise<ReVerificationGate> => {
  const core = getReadOnlyContract("ecofutures-core");
  const bank = getReadOnlyContract("ecofutures-bank");
  const token = getReadOnlyContract("ecofutures-token");
  if (!core || !bank || !token) {
    throw new Error("Core, Bank or Token contract not found");
  }
  const id = BigInt(tokenId);
  const [owner, openChallenge, easement, frozen, latest] = await Promise.all([
    token.ownerOf(id).then(String),
    hasOpenChallenge(id),
    core.getEasement(id),
    bank.dripFrozen(id).then(Boolean),
    getReadOnlyProvider().getBlock("latest"),
  ]);
  const vestingStart = Number(easement.easementVestingStartTime);
  const interval = Number(easement.releaseFundInterval);
  const released = Number(easement.releasedCount);
  const nextDueAt =
    vestingStart > 0 && interval > 0 && !frozen ? new Date((vestingStart + (released + 1) * interval) * 1000) : null;
  return {
    bankHoldsEft: owner.toLowerCase() === ecoFuturesBankContractAddress.toLowerCase(),
    openChallenge,
    nextDueAt,
    chainClockOffsetMs: latest ? Number(latest.timestamp) * 1000 - Date.now() : 0,
  };
};

// Hook form. Pass the on-chain NFT id (etNft.id), or null/undefined to skip (pre-mint pages).
const useReVerificationGate = (tokenId: number | string | null | undefined) => {
  const [gate, setGate] = useState<ReVerificationGate | null>(null);

  useEffect(() => {
    setGate(null);
    if (tokenId === null || tokenId === undefined || tokenId === "") {
      return;
    }
    let cancelled = false;
    fetchReVerificationGate(tokenId)
      .then((g) => {
        if (!cancelled) {
          setGate(g);
        }
      })
      .catch((e) => console.warn("re-verification gate read failed:", e));
    return () => {
      cancelled = true;
    };
  }, [tokenId]);

  return gate;
};

// When the gate's instalment falls due on the viewer's clock, and whether Verify is held back now.
// `dueReached` flips once, on a timeout, so a caller does not re-render every second for a
// countdown — anything that ticks should be its own small component.
export const useReVerificationDue = (gate: ReVerificationGate | null) => {
  // Wall-clock ms at which the next instalment is due (chain time already corrected), or null.
  const dueAtMs = gate?.nextDueAt ? gate.nextDueAt.getTime() - gate.chainClockOffsetMs : null;
  const [dueReached, setDueReached] = useState(true);

  useEffect(() => {
    if (dueAtMs === null || dueAtMs <= Date.now()) {
      setDueReached(true);
      return;
    }
    setDueReached(false);
    const wait = dueAtMs - Date.now();
    // setTimeout's delay is a signed 32-bit int (~24.8 days). Beyond that, arm nothing rather than
    // fire early — the page is reloaded long before a production-length interval runs out.
    if (wait > 2 ** 31 - 1) {
      return;
    }
    const timer = window.setTimeout(() => setDueReached(true), wait);
    return () => window.clearTimeout(timer);
  }, [dueAtMs]);

  return { dueAtMs, dueReached, blocked: !!gate && (gate.openChallenge || !dueReached) };
};

export default useReVerificationGate;
